(function (root, factory) {
  'use strict';
  const model = factory();
  if (typeof module === 'object' && module.exports) module.exports = model;
  else root.ReplayWorkshopModel = model;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const duration = 58000;
  const speeds = Object.freeze([0.5, 1, 2, 4]);
  const idle = Object.freeze({ start: 23000, end: 43000 });
  const moments = Object.freeze([
    { id: 'product', time: 0, kind: 'page', label: 'Viewed Daily Cleanser', detail: 'Product page', page: 'product', path: '/products/daily-cleanser', description: 'The sample shopper opens the Daily Cleanser product page.' },
    { id: 'add', time: 9000, kind: 'cart', label: 'Added to cart', detail: 'Daily Cleanser · $48', page: 'product', path: '/products/daily-cleanser', description: 'One Daily Cleanser is added to the cart.' },
    { id: 'cart', time: 12500, kind: 'page', label: 'Opened the cart', detail: '1 item · $48 subtotal', page: 'cart', path: '/cart', description: 'The cart shows one item. Delivery cost has not been added to the subtotal.' },
    { id: 'shipping', time: 18000, kind: 'page', label: 'Opened shipping information', detail: 'Delivery cost and timing', page: 'shipping', path: '/pages/shipping', description: 'The shopper opens the shipping page. It lists $6 standard delivery and a 3–5 business-day estimate.' },
    { id: 'return', time: 44000, kind: 'page', label: 'Returned to the cart', detail: '1 item · $48 subtotal', page: 'cart', path: '/cart', description: 'The shopper returns to the cart. The pointer rests on Checkout from 0:48 to 0:51 without a click. The replay cannot tell us what they are thinking.' },
    { id: 'end', time: 58000, kind: 'end', label: 'Visit ended', detail: 'No checkout event in this sample', page: 'cart', path: '/cart', description: 'The simulated visit ends. There is no checkout event in this sample; the reason is unknown.' }
  ].map(Object.freeze));
  // A synthetic pointer track with pauses, small reading movements and clicks
  // on the actual control before each navigation. Page changes reset its frame
  // of reference, as a replay does; ordinary movement remains continuous.
  const cursorSteps = Object.freeze([
    { time: 0, page: 'product', anchor: 'product-photo', x: .28, y: .33 },
    { time: 1100, page: 'product', anchor: 'product-photo', x: .45, y: .4 },
    { time: 1900, page: 'product', anchor: 'product-photo', x: .46, y: .42 },
    { time: 3200, page: 'product', anchor: 'product-title', x: .54, y: .72 },
    { time: 4700, page: 'product', anchor: 'product-title', x: .59, y: .78 },
    { time: 6200, page: 'product', anchor: 'add-cart', x: .7, y: .52 },
    { time: 7700, page: 'product', anchor: 'add-cart', x: .59, y: .55 },
    { time: 9000, page: 'product', anchor: 'add-cart', x: .6, y: .55, click: true },
    { time: 10100, page: 'product', anchor: 'add-cart', x: .62, y: .51 },
    { time: 11700, page: 'product', anchor: 'cart-open', x: .6, y: .54 },
    { time: 12100, page: 'product', anchor: 'cart-open', x: .62, y: .52, click: true },
    { time: 12500, page: 'cart', anchor: 'cart-item', x: .72, y: .44 },
    { time: 13900, page: 'cart', anchor: 'cart-total', x: .73, y: .5 },
    { time: 15600, page: 'cart', anchor: 'shipping-link', x: .6, y: .52 },
    { time: 17500, page: 'cart', anchor: 'shipping-link', x: .63, y: .54, click: true },
    { time: 18000, page: 'shipping', anchor: 'shipping-title', x: .5, y: .63 },
    { time: 19600, page: 'shipping', anchor: 'shipping-cost', x: .64, y: .24 },
    { time: 21500, page: 'shipping', anchor: 'shipping-cost', x: .72, y: .5 },
    { time: 22500, page: 'shipping', anchor: 'shipping-cost', x: .71, y: .51 },
    { time: 43000, page: 'shipping', anchor: 'shipping-cost', x: .71, y: .51 },
    { time: 43700, page: 'shipping', anchor: 'return-cart', x: .49, y: .52, click: true },
    { time: 44000, page: 'cart', anchor: 'cart-item', x: .75, y: .6 },
    { time: 46300, page: 'cart', anchor: 'cart-total', x: .8, y: .6 },
    { time: 48400, page: 'cart', anchor: 'checkout', x: .54, y: .6 },
    { time: 50700, page: 'cart', anchor: 'checkout', x: .64, y: .58 },
    { time: 52800, page: 'cart', anchor: 'cart-total', x: .62, y: .73 },
    { time: 55400, page: 'cart', anchor: 'cart-item', x: .65, y: .74 },
    { time: duration, page: 'cart', anchor: 'cart-item', x: .65, y: .74 }
  ].map(Object.freeze));
  const scrollSteps = Object.freeze([
    { time: 0, amount: 0 }, { time: 4000, amount: 0 },
    { time: 5200, amount: 24 }, { time: 9800, amount: 24 },
    { time: 11300, amount: 0 }, { time: 18000, amount: 0 },
    { time: 20100, amount: 0 }, { time: 21500, amount: 20 },
    { time: 43000, amount: 20 }, { time: 43700, amount: 0 },
    { time: duration, amount: 0 }
  ].map(Object.freeze));

  function betweenSteps(steps, time) {
    const index = steps.reduce((last, step, i) => step.time <= time ? i : last, 0);
    const from = steps[index];
    const to = steps[index + 1] || from;
    const progress = to.time === from.time ? 0 : Math.max(0, Math.min(1, (time - from.time) / (to.time - from.time)));
    // Smooth acceleration prevents the sample from looking like six teleporting
    // slide changes, while keeping every scrub target completely deterministic.
    return { from, to, progress: progress * progress * (3 - 2 * progress) };
  }

  function clampTime(value) {
    const time = Number(value);
    return Number.isFinite(time) ? Math.max(0, Math.min(duration, time)) : 0;
  }

  function formatTime(value) {
    const seconds = Math.floor(clampTime(value) / 1000);
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  }

  function stateAt(value) {
    const time = clampTime(value);
    const activeIndex = moments.reduce((last, moment, index) => moment.time <= time ? index : last, 0);
    const moment = moments[activeIndex];
    const pointer = betweenSteps(cursorSteps, time);
    if (pointer.to.page !== pointer.from.page) pointer.to = pointer.from;
    const scroll = betweenSteps(scrollSteps, time);
    const clicked = Boolean(pointer.from.click && time < pointer.from.time + 420);
    const scrolling = scroll.from.amount !== scroll.to.amount && scroll.progress > 0 && scroll.progress < 1;
    const eventLabel = time >= duration ? 'Visit ended'
      : time >= idle.start && time < idle.end ? 'No recorded interaction'
      : clicked ? ({ 'add-cart': 'Clicked Add to cart', 'cart-open': 'Opened the cart', 'shipping-link': 'Clicked delivery information', 'return-cart': 'Clicked Back to your cart' }[pointer.from.anchor] || 'Clicked the page')
      : scrolling ? 'Scrolled the page'
      : moment.page === 'shipping' ? 'Reading delivery information'
      : moment.page === 'cart' ? 'Reviewing the cart' : 'Exploring the product';
    return {
      time, activeIndex, moment, page: moment.page, path: moment.path,
      cartCount: time >= 9000 ? 1 : 0,
      added: time >= 9000 && time < 12500,
      ended: time >= duration,
      idle: time >= idle.start && time < idle.end,
      cursor: pointer.from.anchor, pointer,
      scroll: scroll.from.amount + (scroll.to.amount - scroll.from.amount) * scroll.progress,
      clicked, eventLabel
    };
  }

  // Only playback skips idle. A merchant can still seek into the gap to inspect it.
  function advance(value, elapsed, speed, skipIdle) {
    const before = clampTime(value);
    const rate = speeds.includes(speed) ? speed : 1;
    const delta = Number.isFinite(elapsed) ? Math.max(0, elapsed) * rate : 0;
    let next = Math.min(duration, before + delta);
    let skipped = 0;
    if (skipIdle && before < idle.end && next >= idle.start) {
      skipped = idle.end - Math.max(before, idle.start);
      // Keep only the portion of this frame beyond the idle boundary.
      next = Math.min(duration, Math.max(idle.end, next + skipped));
    }
    return { time: next, skipped, ended: next >= duration };
  }

  // What a person watching this visit can actually see. The pause and the
  // Checkout hover are the clues; the visit itself never says why.
  const observation = 'At 0:18 the shopper opened delivery information from the cart. From 0:23 to 0:43 there was no interaction; the pointer stayed on the $6 delivery cost. At 0:44 they returned to the cart, and from 0:48 to 0:51 the pointer rested on Checkout without a click. The visit ended at 0:58 with no checkout event.';
  const defaultNextCheck = 'Check whether the delivery cost is visible in the cart, not only on a separate page, and whether other visits pause on it too.';

  // A template for the merchant's OWN recordings. The Solara visit appears only
  // as a labelled worked example, so the note is never about someone else's store.
  function createNote(nextCheck) {
    const action = typeof nextCheck === 'string' ? nextCheck.trim().slice(0, 1200) : '';
    if (!action) return null;
    return [
      'SESSION REPLAY REVIEW NOTE',
      'myuser.ai · Session replay demo',
      'Fill this in while you watch a recording from your own store.',
      '',
      'QUESTION',
      'What do you want to learn from this visit?',
      '',
      'WHAT YOU SAW (page · time · action)',
      '- ',
      '- ',
      '',
      'POSSIBLE EXPLANATION (a guess to test, not a fact)',
      '- ',
      '',
      'NEXT CHECK', action,
      '',
      'BEFORE CHANGING YOUR STORE',
      'Look for the same pattern in more visits and check your store directly. A replay shows actions, not a shopper’s reason or a guaranteed sales outcome.',
      '',
      'WORKED EXAMPLE (a simulated 58-second Solara visit, no real customer)',
      'Question: Does the delivery cost show up too late?',
      'What we saw: ' + observation,
      'Possible explanation: The $6 delivery cost may have surprised them. This visit does not establish whether the cost, timing, or anything else caused them to leave.',
      '',
      'https://myuser.ai/tools/session-replay-demo'
    ].join('\n');
  }

  return Object.freeze({ duration, speeds, idle, moments, clampTime, formatTime, stateAt, advance, observation, defaultNextCheck, createNote });
});
